/* global axios */
import ApiClient from './ApiClient';

const getTimeOffset = () => -new Date().getTimezoneOffset() / 60;

class ReportsAPI extends ApiClient {
  constructor() {
    super('reports', { accountScoped: true, apiVersion: 'v2' });
  }

  getReports({
    metric,
    from,
    to,
    type = 'account',
    id,
    groupBy,
    businessHours,
  }) {
    return axios.get(`${this.url}`, {
      params: {
        metric,
        since: from,
        until: to,
        type,
        id,
        group_by: groupBy,
        business_hours: businessHours,
        timezone_offset: getTimeOffset(),
      },
    });
  }

  // eslint-disable-next-line default-param-last
  getSummary(since, until, type = 'account', id, groupBy, businessHours) {
    return axios.get(`${this.url}/summary`, {
      params: {
        since,
        until,
        type,
        id,
        group_by: groupBy,
        business_hours: businessHours,
        timezone_offset: getTimeOffset(),
      },
    });
  }

  getConversationMetric(type = 'account', page = 1) {
    return axios.get(`${this.url}/conversations`, {
      params: {
        type,
        page,
      },
    });
  }

  getAgentReports({ from: since, to: until, businessHours }) {
    return axios.get(`${this.url}/agents`, {
      params: { since, until, business_hours: businessHours },
    });
  }

  getConversationsSummaryReports({ from: since, to: until, businessHours }) {
    return axios.get(`${this.url}/conversations_summary`, {
      params: { since, until, business_hours: businessHours },
    });
  }

  getConversationTrafficCSV({ daysBefore = 6 } = {}) {
    return axios.get(`${this.url}/conversation_traffic`, {
      params: { timezone_offset: getTimeOffset(), days_before: daysBefore },
    });
  }

  getLabelReports({ from: since, to: until, businessHours }) {
    return axios.get(`${this.url}/labels`, {
      params: { since, until, business_hours: businessHours },
    });
  }

  getInboxReports({ from: since, to: until, businessHours }) {
    return axios.get(`${this.url}/inboxes`, {
      params: { since, until, business_hours: businessHours },
    });
  }

  getTeamReports({ from: since, to: until, businessHours }) {
    return axios.get(`${this.url}/teams`, {
      params: { since, until, business_hours: businessHours },
    });
  }

  getBotMetrics({ from, to } = {}) {
    return axios.get(`${this.url}/bot_metrics`, {
      params: { since: from, until: to },
    });
  }

  getConversationDistribution({ from, to } = {}) {
    return axios.get(`${this.url}/conversation_distribution`, {
      params: { since: from, until: to },
    });
  }

  getBotSummary({ from, to, groupBy, businessHours } = {}) {
    return axios.get(`${this.url}/bot_summary`, {
      params: {
        since: from,
        until: to,
        type: 'account',
        group_by: groupBy,
        business_hours: businessHours,
      },
    });
  }

  getOperatingSchedule() {
    return axios.get(`${this.url}/operating_schedule`);
  }

  getLiveCockpit() {
    return axios.get(`${this.url}/live_cockpit`);
  }

  saveLiveCockpitGoals({
    goalValue,
    goalDeals,
    entryStageIds,
    winStageIds,
    operatingSchedule,
    staleDays,
  }) {
    return axios.post(`${this.url}/live_cockpit_goals`, {
      goal_value: goalValue,
      goal_deals: goalDeals,
      entry_stage_ids: entryStageIds,
      win_stage_ids: winStageIds,
      operating_schedule: operatingSchedule,
      stale_days: staleDays,
    });
  }

  getConversationTimePercentiles({ from, to, businessHours, groupBy } = {}) {
    return axios.get(`${this.url}/conversation_time_percentiles`, {
      params: {
        since: from,
        until: to,
        business_hours: businessHours,
        group_by: groupBy,
      },
    });
  }

  getAgentAvailability({ from, to } = {}) {
    return axios.get(`${this.url}/agent_availability`, {
      params: { since: from, until: to },
    });
  }

  getInboxInsights({ from, to, inboxId, businessHours } = {}) {
    return axios.get(`${this.url}/inbox_insights`, {
      params: {
        since: from,
        until: to,
        inbox_id: inboxId,
        business_hours: businessHours,
      },
    });
  }

  getTeamInsights({ from, to, teamId, businessHours } = {}) {
    return axios.get(`${this.url}/team_insights`, {
      params: {
        since: from,
        until: to,
        team_id: teamId,
        business_hours: businessHours,
      },
    });
  }

  getLabelInsights({ from, to, title, businessHours } = {}) {
    return axios.get(`${this.url}/label_insights`, {
      params: {
        since: from,
        until: to,
        title,
        business_hours: businessHours,
      },
    });
  }

  getBotInsights({ from, to, assistantId, groupBy } = {}) {
    return axios.get(`${this.url}/bot_insights`, {
      params: {
        since: from,
        until: to,
        assistant_id: assistantId,
        group_by: groupBy,
        timezone_offset: getTimeOffset(),
      },
    });
  }

  getCopilotInsights({ from, to, groupBy } = {}) {
    return axios.get(`${this.url}/copilot_insights`, {
      params: {
        since: from,
        until: to,
        group_by: groupBy,
        timezone_offset: getTimeOffset(),
      },
    });
  }

  getAgentAvailabilityHeatmap({ from, to, userId } = {}) {
    return axios.get(`${this.url}/agent_availability`, {
      params: { since: from, until: to, user_id: userId, view: 'heatmap' },
    });
  }
}

export default new ReportsAPI();
